// Fase 8 — Operação (mocked prototype)
import React from 'react';
import { Route, Map, CalendarDays, Activity, GitBranch, Users, Building2, Clock, Navigation, FileText, TrendingUp, Truck } from 'lucide-react';
import { MockPageShell, MockKpi, MockBar, MockLine, MockPie, ChartCard, MockToolbar, MockTable, StatusBadge } from '../../components/MockUI';
import { mockRoutes, mockTrips, mockEmployees, genPie, genSeries } from '../../lib/mockData';
import { Button } from '../../components/ui/button';

const bc = (l) => [{ label: 'Operação', to: '/operacao' }, { label: l }];

export function OperacaoDashboard() {
  return <MockPageShell title="Dashboard Operacional" subtitle="Viagens, rotas e alocação em tempo real" breadcrumbs={[{ label: 'Operação' }]}
    kpis={[
      <MockKpi key="1" icon={Activity} label="Viagens hoje" value="128" tone="success" />,
      <MockKpi key="2" icon={Truck} label="Veículos em rota" value="24" />,
      <MockKpi key="3" icon={Route} label="Rotas ativas" value="42" />,
      <MockKpi key="4" icon={Users} label="Motoristas alocados" value="38" />,
    ]}
    charts={[
      <ChartCard key="1" title="Viagens por dia" subtitle="Últimos 6 meses"><MockLine data={genSeries(120, 30)} /></ChartCard>,
      <ChartCard key="2" title="Ocupação por categoria"><MockPie data={genPie(['Fretamento contínuo', 'Turismo', 'Escolar', 'Rota corporativa', 'Eventual'])} /></ChartCard>,
    ]}
  />;
}

export function Linhas() {
  const rows = [
    { code: 'L-01', name: 'Linha Interbairros Zona Sul', vehicle: 'ABC-1234', route: 'R-001', frequency: 'Diária', status: 'Ativa' },
    { code: 'L-02', name: 'Linha Escolar Colégio Santa Rita', vehicle: 'PQR-2468', route: 'R-004', frequency: 'Seg-Sex', status: 'Ativa' },
    { code: 'L-03', name: 'Linha Turismo Serra', vehicle: 'JKL-3456', route: 'R-003', frequency: 'Fim de semana', status: 'Ativa' },
    { code: 'L-04', name: 'Linha Corporativa UFRGS', vehicle: 'DEF-5678', route: 'R-002', frequency: 'Ter/Qui', status: 'Suspensa' },
  ];
  return <MockPageShell title="Linhas" subtitle="Linhas fixas de operação" breadcrumbs={bc('Linhas')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova linha</Button>}
    table={<MockTable columns={[{ key: 'code', label: 'Código', mono: true }, { key: 'name', label: 'Nome' }, { key: 'vehicle', label: 'Veículo', mono: true }, { key: 'route', label: 'Rota', mono: true }, { key: 'frequency', label: 'Frequência' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Ativa: 'green', Suspensa: 'red' }} />}
  />;
}

export function Rotas() {
  return <MockPageShell title="Rotas" subtitle="Trajetos cadastrados e mapeados" breadcrumbs={bc('Rotas')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova rota</Button>}
    extra={<MockToolbar />}
    table={<MockTable columns={[
      { key: 'code', label: 'Código', mono: true }, { key: 'name', label: 'Trajeto' },
      { key: 'vehicle', label: 'Veículo', mono: true }, { key: 'driver', label: 'Motorista' },
      { key: 'distance', label: 'Km', align: 'right' }, { key: 'duration', label: 'Duração' },
      { key: 'status', label: 'Status', status: true },
    ]} rows={mockRoutes} statusMap={{ Ativa: 'green' }} />}
  />;
}

export function Escalas() {
  const days = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];
  const drivers = mockEmployees.filter(e => e.role === 'Motorista').slice(0, 5);
  return <MockPageShell title="Escalas" subtitle="Grade semanal de motoristas × turnos" breadcrumbs={bc('Escalas')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova escala</Button>}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5 overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="text-left py-2 text-xs text-neutral-500 uppercase">Motorista</th>
            {days.map(d => <th key={d} className="text-center py-2 text-xs text-neutral-500 uppercase">{d}</th>)}
          </tr>
        </thead>
        <tbody>
          {drivers.map(dr => (
            <tr key={dr.id} className="border-t border-neutral-100 dark:border-neutral-800">
              <td className="py-3 font-medium">{dr.name}</td>
              {days.map((d, i) => {
                const shifts = ['06-14', '14-22', '22-06', 'FOLGA', '06-14'];
                const s = shifts[(i + dr.id.charCodeAt(1)) % shifts.length];
                return <td key={d} className="py-3 text-center">
                  <span className={`inline-flex px-2 py-1 rounded text-[11px] font-medium ${s === 'FOLGA' ? 'bg-neutral-200 dark:bg-neutral-800 text-neutral-500' : s === '06-14' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : s === '14-22' ? 'bg-amber-100 text-amber-800' : 'bg-indigo-100 text-indigo-700'}`}>{s}</span>
                </td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>}
  />;
}

export function Agenda() {
  return <MockPageShell title="Agenda Operacional" subtitle="Eventos, saídas e retornos" breadcrumbs={bc('Agenda')}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <h3 className="font-semibold mb-3">Hoje — 10/02/2026</h3>
      <ol className="relative border-l-2 border-neutral-200 dark:border-neutral-800 ml-2 space-y-3">
        {mockTrips.map((t, i) => (
          <li key={i} className="pl-6 relative">
            <span className={`absolute -left-[9px] top-1 h-4 w-4 rounded-full border-2 border-white dark:border-neutral-900 ${t.status === 'Concluída' ? 'bg-emerald-500' : t.status === 'Em andamento' ? 'bg-blue-500' : 'bg-amber-500'}`} />
            <div className="text-sm font-medium">{t.code} — {t.route}</div>
            <div className="text-xs text-neutral-500 mt-0.5">{t.departure} → {t.arrival} • {t.driver} • {t.vehicle}</div>
            <StatusBadge value={t.status} tone={t.status === 'Concluída' ? 'green' : t.status === 'Em andamento' ? 'blue' : 'amber'} />
          </li>
        ))}
      </ol>
    </div>}
  />;
}

export function Viagens() {
  return <MockPageShell title="Viagens" subtitle="Registro operacional de viagens" breadcrumbs={bc('Viagens')}
    right={<Button className="bg-brand hover:bg-[#A81E23] text-white">Nova viagem</Button>}
    kpis={[
      <MockKpi key="1" icon={Activity} label="Total (mês)" value="1.842" />,
      <MockKpi key="2" icon={GitBranch} label="Concluídas" value="1.708" tone="success" />,
      <MockKpi key="3" icon={Clock} label="Em andamento" value="24" tone="info" />,
      <MockKpi key="4" icon={GitBranch} label="Canceladas" value="12" tone="critical" />,
    ]}
    table={<MockTable columns={[
      { key: 'code', label: 'Nº', mono: true }, { key: 'route', label: 'Rota', mono: true }, { key: 'vehicle', label: 'Veículo', mono: true },
      { key: 'driver', label: 'Motorista' }, { key: 'date', label: 'Data' }, { key: 'departure', label: 'Saída' },
      { key: 'arrival', label: 'Chegada' }, { key: 'status', label: 'Status', status: true },
    ]} rows={mockTrips} statusMap={{ Concluída: 'green', 'Em andamento': 'blue', Agendada: 'amber' }} />}
  />;
}

export function Programacao() {
  return <MockPageShell title="Programação Semanal" subtitle="Planejamento de recursos operacionais" breadcrumbs={bc('Programação')}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <h3 className="font-semibold mb-3">Semana de 10 a 16/02/2026</h3>
      <div className="grid grid-cols-7 gap-2">
        {['Seg 10', 'Ter 11', 'Qua 12', 'Qui 13', 'Sex 14', 'Sáb 15', 'Dom 16'].map((d, i) => (
          <div key={d} className="rounded-lg border border-neutral-200 dark:border-neutral-800 p-3 min-h-[160px]">
            <div className="text-xs font-semibold text-neutral-500 mb-2">{d}</div>
            {Array.from({ length: 2 + i % 3 }).map((_, j) => (
              <div key={j} className={`text-[10px] px-1.5 py-1 rounded mb-1 ${j % 3 === 0 ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300' : j % 3 === 1 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-amber-100 text-amber-800'}`}>
                {['R-001', 'R-002', 'R-003', 'R-004'][j % 4]} • {['C. Silva', 'J. Ferreira', 'A. Souza'][j % 3]}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>}
  />;
}

export function Disponibilidade() {
  const rows = mockRoutes.map(r => ({ ...r, occupation: `${Math.round(50 + Math.random() * 40)}%`, available: Math.random() > 0.3 ? 'Sim' : 'Não' }));
  return <MockPageShell title="Disponibilidade de recursos" breadcrumbs={bc('Disponibilidade')}
    kpis={[
      <MockKpi key="1" icon={Truck} label="Veículos disponíveis" value="18" tone="success" />,
      <MockKpi key="2" icon={Users} label="Motoristas disponíveis" value="26" />,
      <MockKpi key="3" icon={Activity} label="Taxa de ocupação" value="82%" tone="info" />,
    ]}
    table={<MockTable columns={[{ key: 'code', label: 'Rota', mono: true }, { key: 'name', label: 'Nome' }, { key: 'vehicle', label: 'Veículo', mono: true }, { key: 'driver', label: 'Motorista' }, { key: 'occupation', label: 'Ocupação', align: 'right' }, { key: 'available', label: 'Disponível' }]} rows={rows} />}
  />;
}

export function ContratosOp() {
  const rows = [
    { code: 'CT-001', client: 'UFRGS', kind: 'Fretamento contínuo', start: '01/03/2025', end: '28/02/2026', value: 'R$ 540.000', status: 'Ativo' },
    { code: 'CT-002', client: 'Prefeitura de Canoas', kind: 'Contrato público', start: '15/06/2024', end: '14/06/2027', value: 'R$ 1.480.000', status: 'Ativo' },
    { code: 'CT-003', client: 'Colégio Santa Rita', kind: 'Escolar', start: '01/02/2026', end: '20/12/2026', value: 'R$ 128.000', status: 'Ativo' },
    { code: 'CT-004', client: 'Vinícola Aurora', kind: 'Turismo corporativo', start: '01/01/2026', end: '31/12/2026', value: 'R$ 78.000', status: 'Ativo' },
  ];
  return <MockPageShell title="Contratos Operacionais" breadcrumbs={bc('Contratos')}
    table={<MockTable columns={[{ key: 'code', label: 'Nº', mono: true }, { key: 'client', label: 'Cliente' }, { key: 'kind', label: 'Modalidade' }, { key: 'start', label: 'Início' }, { key: 'end', label: 'Fim' }, { key: 'value', label: 'Valor', align: 'right' }, { key: 'status', label: 'Status', status: true }]} rows={rows} statusMap={{ Ativo: 'green', Encerrado: 'neutral' }} />}
  />;
}

export function Calendario() {
  return <MockPageShell title="Calendário" subtitle="Visão mensal" breadcrumbs={bc('Calendário')}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 p-5">
      <h3 className="font-semibold mb-3">Fevereiro / 2026</h3>
      <div className="grid grid-cols-7 gap-1">
        {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(d => <div key={d} className="text-center text-[11px] font-semibold text-neutral-500 py-2">{d}</div>)}
        {Array.from({ length: 35 }).map((_, i) => {
          const day = i - 3;
          const active = day > 0 && day <= 28;
          const has = active && (day % 3 === 0 || day % 5 === 0);
          return <div key={i} className={`aspect-square rounded border p-1.5 ${active ? 'border-neutral-200 dark:border-neutral-800' : 'border-transparent'} ${day === 10 ? 'ring-2 ring-brand' : ''}`}>
            {active && <div className="text-xs">{day}</div>}
            {has && <div className="mt-1 flex flex-wrap gap-0.5">
              <span className="text-[8px] px-1 rounded bg-blue-100 text-blue-700">3 rotas</span>
              {day % 5 === 0 && <span className="text-[8px] px-1 rounded bg-emerald-100 text-emerald-700">Turismo</span>}
            </div>}
          </div>;
        })}
      </div>
    </div>}
  />;
}

export function MapaOperacional() {
  return <MockPageShell title="Mapa Operacional" subtitle="Localização em tempo real (mockado)" breadcrumbs={bc('Mapa Operacional')}
    extra={<div className="rounded-xl bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 overflow-hidden">
      <div className="relative h-[500px] bg-gradient-to-br from-blue-50 via-emerald-50 to-neutral-100 dark:from-blue-950/30 dark:via-emerald-950/30 dark:to-neutral-900">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <Map className="h-16 w-16 mx-auto text-neutral-400" />
            <p className="text-neutral-500 mt-3 text-sm">Mapa integrado (Google Maps / Leaflet) — placeholder visual</p>
          </div>
        </div>
        {/* Simulated pins */}
        {[
          { top: '20%', left: '30%', label: 'ABC-1234', tone: 'bg-emerald-500' },
          { top: '35%', left: '55%', label: 'DEF-5678', tone: 'bg-blue-500' },
          { top: '55%', left: '25%', label: 'GHI-9012', tone: 'bg-blue-500' },
          { top: '65%', left: '70%', label: 'JKL-3456', tone: 'bg-amber-500' },
          { top: '75%', left: '45%', label: 'PQR-2468', tone: 'bg-emerald-500' },
        ].map((p, i) => (
          <div key={i} className="absolute" style={{ top: p.top, left: p.left }}>
            <div className={`h-3 w-3 rounded-full ${p.tone} ring-4 ring-white/60 animate-pulse`} />
            <span className="text-[10px] font-mono absolute top-4 left-1/2 -translate-x-1/2 whitespace-nowrap bg-white/90 dark:bg-neutral-900/90 px-1 py-0.5 rounded">{p.label}</span>
          </div>
        ))}
      </div>
    </div>}
  />;
}
